import React, { useCallback } from "react";
import { useTranslation } from "react-i18next";
import { Body2 } from "@activecollab/components";
import PropTypes from "prop-types";
import { PublicInvoiceObjectAmount } from "~/components/InvoiceObject/PublicInvoiceObjectAmount";

export const PublicInvoiceObjectSubtotalPreview = ({ item, items, currency }) => {
  const { t } = useTranslation();

  const getRates = useCallback(
    (prefix) => {
      const result = items.reduce((prev, next) => {
        const key = `${prefix}_tax_rate_id`;
        const id = next[key];
        if (id) {
          const previous = prev[id] || {
            value: 0,
          };
          prev[id] = {
            ...previous,
            name: next[`${prefix}_tax_name`],
            value: previous.value + next[`${prefix}_tax_value`],
            id: id,
            rate: next[`${prefix}_tax_rate`],
          };
        }
        return prev;
      }, {});
      return Object.values(result);
    },
    [items]
  );

  return (
    <table className="invoice_total_table tw-table-auto tw-border-none">
      <tbody>
        <tr className="row">
          <td>
            <Body2 align="right">{t("Subtotal")}</Body2>
          </td>
          <td>
            <Body2>
              <PublicInvoiceObjectAmount
                currency={currency}
                total={item.subtotal}
                showCurrency={false}
                trimDecimals={false}
                format="long"
              />
            </Body2>
          </td>
        </tr>
        {item.discount_rate > 0 ? (
          <tr className="row">
            <td>
              <Body2 align="right">
                {t("Discount")} {`(${item.discount_rate}%)`}
              </Body2>
            </td>
            <td>
              <Body2>
                -
                <PublicInvoiceObjectAmount
                  currency={currency}
                  total={item.discount}
                  showCurrency={false}
                  trimDecimals={false}
                  format="long"
                />
              </Body2>
            </td>
          </tr>
        ) : null}
        {getRates("first")?.map((rate, index) => {
          return (
            <tr
              className="row"
              key={index}
            >
              <td>
                <Body2 align="right">
                  {rate.name} ({rate.rate}%)
                </Body2>
              </td>
              <td>
                <Body2>
                  <PublicInvoiceObjectAmount
                    currency={currency}
                    total={rate.value}
                    showCurrency={false}
                    trimDecimals={false}
                    format="long"
                  />
                </Body2>
              </td>
            </tr>
          );
        })}
        {getRates("second")?.map((rate, index) => {
          return (
            <tr
              className="row"
              key={index}
            >
              <td>
                <Body2 align="right">
                  {rate.name} ({rate.rate}%)
                </Body2>
              </td>
              <td>
                <Body2>
                  <PublicInvoiceObjectAmount
                    currency={currency}
                    total={rate.value}
                    showCurrency={false}
                    trimDecimals={false}
                    format="long"
                  />
                </Body2>
              </td>
            </tr>
          );
        })}
        {item.rounding_difference > 0 ? (
          <tr className="row">
            <td>
              <Body2 align="right">{t("Rounding Diff.")}</Body2>
            </td>
            <td className="numeric amount">
              <Body2>
                <PublicInvoiceObjectAmount
                  currency={currency}
                  languageId={item.language_id}
                  total={item.rounding_difference}
                  showCurrency={false}
                  trimDecimals={false}
                  format="long"
                />
              </Body2>
            </td>
          </tr>
        ) : null}
        <tr className="row tw-font-bold">
          <td>
            <Body2
              weight="bold"
              align="right"
            >
              {t("Total")} ({currency?.code})
            </Body2>
          </td>
          <td>
            <Body2 weight="bold">
              <PublicInvoiceObjectAmount
                currency={currency}
                languageId={item.language_id}
                total={item.total}
                showCurrency={false}
                trimDecimals={false}
                format="long"
              />
            </Body2>
          </td>
        </tr>
        {item.class === "Invoice" ? (
          <>
            <tr className="row">
              <td>
                <Body2 align="right">{t("Amount Paid")}</Body2>
              </td>
              <td className="numeric amount">
                <Body2>
                  <PublicInvoiceObjectAmount
                    currency={currency}
                    languageId={item.language_id}
                    total={item.paid_amount}
                    showCurrency={false}
                    trimDecimals={false}
                    format="long"
                  />
                </Body2>
              </td>
            </tr>
            <tr className="row tw-font-bold">
              <td>
                <Body2
                  weight="bold"
                  align="right"
                >
                  {t("Balance Due")} ({currency?.code})
                </Body2>
              </td>
              <td>
                <Body2 weight="bold">
                  <PublicInvoiceObjectAmount
                    currency={currency}
                    languageId={item.language_id}
                    total={item.balance_due}
                    showCurrency={false}
                    trimDecimals={false}
                    format="long"
                  />
                </Body2>
              </td>
            </tr>
          </>
        ) : null}
      </tbody>
    </table>
  );
};

PublicInvoiceObjectSubtotalPreview.propTypes = {
  items: PropTypes.array,
  item: PropTypes.object,
  currency: PropTypes.object,
};
